"use client";

import * as React from "react";
import { cn } from "@gerama/utils";

interface PlayerProgressProps {
  positionSeconds: number;
  durationSeconds: number;
  onSeek?: (seconds: number) => void;
  className?: string;
}

/**
 * The playback progress bar. Always fills left-to-right, in every locale —
 * the same rule `delkok-app`'s Flutter player already enforces (directional
 * icons mirror for RTL, the progress bar never does, since "elapsed" reading
 * right-to-left would fight users' shared mental model of a scrubber). It
 * force-sets `dir="ltr"` on itself regardless of the page's own direction,
 * so the fill's percentage-width always grows from the physical left edge.
 */
export function PlayerProgress({
  positionSeconds,
  durationSeconds,
  onSeek,
  className,
}: PlayerProgressProps) {
  const ratio = durationSeconds > 0 ? Math.min(1, Math.max(0, positionSeconds / durationSeconds)) : 0;

  function handleClick(event: React.MouseEvent<HTMLDivElement>) {
    if (!onSeek || durationSeconds <= 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const clickRatio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    onSeek(clickRatio * durationSeconds);
  }

  return (
    <div
      dir="ltr"
      role="slider"
      aria-label="پیشرفت پخش"
      aria-valuemin={0}
      aria-valuemax={durationSeconds}
      aria-valuenow={positionSeconds}
      onClick={handleClick}
      className={cn(
        "bg-muted group relative h-1 w-full cursor-pointer overflow-hidden rounded-full",
        className,
      )}
    >
      <div
        className="bg-primary absolute inset-y-0 left-0 rounded-full transition-[width] group-hover:opacity-90"
        style={{ width: `${ratio * 100}%` }}
      />
    </div>
  );
}
